import React from 'react';

import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router-dom';

import { LOCAL_STORAGE_EDIT_PROFILE_PROMPT_SEEN, MainNavigation } from '@pkg/components/MainNavigation';
import { ActionItem, MenuItem, type NavigationArgs } from '@pkg/components/MainNavigation/types';
import { TwitterConnectModal } from '@pkg/components/TwitterConnect/TwitterConnectModal';
import { useAppSelector, useInviteModal } from '@pkg/hooks';

import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

import { MainNavigationMobile } from '../MainNavigationMobile';

import { type LayoutNavigationProps } from './types';

import { MainNavigationWrapper } from './styles';

export const LayoutNavigation = ({
    children,
    hide = false,
    activeMenuItem,
    absolutePositionedLiquidId,
    zIndex,
    className,
    disableLiquidId,
}: LayoutNavigationProps) => {
    const [translate] = useTranslation('dashboard');
    const navigate = useNavigate();

    const isAuthenticated = useAuthTokens((state) => state.isAuthenticated);
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);

    const isEditingProfile = useMatch('/profile/edit');
    const isHome = useMatch('/home');

    const { openModal: openInviteModal } = useInviteModal();

    function openInvites(invites?: NavigationArgs) {
        if (typeof invites === 'number') openInviteModal(invites);
    }

    const handleSelectMenuItem = (item: MenuItem, args?: NavigationArgs) => {
        switch (item) {
            case MenuItem.Profile:
                navigate(`/${primaryYat}`);
                break;
            case MenuItem.Yativerse:
                navigate('/yativerse');
                break;
            case MenuItem.Invite:
                openInvites(args);
                break;
            case MenuItem.GlobalSearch:
                navigate('/search');
                break;
            default:
                navigate('/home');
                break;
        }
    };

    const handleSelectActionItem = (item: ActionItem, args?: NavigationArgs) => {
        switch (item) {
            case ActionItem.CreateYat: {
                navigate(`/create${isHome ? '?ref=home' : ''}`, { state: { enableGoBack: true } });
                break;
            }
            case ActionItem.Wallet: {
                navigate('/wallet');
                break;
            }
            case ActionItem.InviteToYat: {
                openInvites(args);
                break;
            }
        }
    };

    const seenPrompt = localStorage.getItem(LOCAL_STORAGE_EDIT_PROFILE_PROMPT_SEEN);

    const pageMarkup =
        !hide && isAuthenticated && !isEditingProfile ? (
            <>
                <MainNavigationWrapper $zIndex={zIndex} className={className}>
                    <MainNavigation
                        activeItem={activeMenuItem}
                        onSelectMenuItem={handleSelectMenuItem}
                        onSelectActionItem={handleSelectActionItem}
                        createYatsButtonText={translate('create_yats')}
                        notifyDotProfile={!seenPrompt}
                        absolutePositionedLiquidId={absolutePositionedLiquidId}
                        disableLiquidId={disableLiquidId}
                    />
                </MainNavigationWrapper>

                <MainNavigationMobile
                    activeItem={activeMenuItem}
                    onSelectActionItem={handleSelectActionItem}
                    onSelectMenuItem={handleSelectMenuItem}
                    createYatsButtonText={translate('create_yats')}
                    notifyDotProfile={!seenPrompt}
                />

                <TwitterConnectModal />
            </>
        ) : null;

    return (
        <>
            {pageMarkup}
            {children}
        </>
    );
};
